import { getSessionId } from '@fs/zion-session'
import { getFrontendBaseUrl } from '../../../util/UrlUtil'

/**
 * Helper to change the context on a DAS URL and append the current session ID.
 * Needed for accessing artifacts are not publicly accessible (unscreened or
 * retrieved with impersonation).
 * @export
 * @param {string} url
 * @returns {string} URL with ?ctx=ArtCtxOps&session={sessionId}
 */
export function sessionifyDASUrl(url) {
  if (typeof url === 'string' && url.includes('ctx=ArtCtxPublic')) {
    return url.replace('ctx=ArtCtxPublic', `ctx=ArtCtxOps&session=${getSessionId()}`)
  }
  return url
}

/**
 * Helper to modify the URLs in an artifact to include the current session ID.
 * Needed for accessing artifacts are not publicly accessible (unscreened or
 * retrieved with impersonation).
 * @export
 * @param {Object} artifact
 * @returns {Object} copy of the artifact with modified URLs
 */
export function sessionifyArtifactUrls(artifact) {
  const newArtifact = { ...artifact }
  Object.keys(newArtifact).forEach((key) => {
    if (key.toLowerCase().includes('url')) {
      newArtifact[key] = sessionifyDASUrl(newArtifact[key])
    }
  })
  return newArtifact
}

/**
 * Parses an artifact event string into an object describing the event
 * Expects string to be of format “RESTRICTED by Patron: 123. Date:  Wed May 22 22:34:02 UTC 2019”
 * @export
 * @param {string} event string to parse
 * @returns {?Object} event object containing action (string), patronId (number), and date (Date object), or null if event was invalid
 */
export function parseEvent(event) {
  event.date = new Date(event.date)
}

/**
 * Truncate a string to the given length and append …
 * Exported so it can be used in tests
 * @param {string} str
 * @param {number} length
 */
export function truncateString(str, length) {
  if (str.length <= length) {
    return str
  }
  return `${str.substring(0, length).trim()}…`
}

/**
 * Convert audio length (width) in artifact to minute:seconds format
 * @returns {string} audio length formatted
 */
export function formatArtifactAudioDuration(artifactAudioLength) {
  const artifactAudioLengthNumber = Number(artifactAudioLength)
  if (Number.isNaN(artifactAudioLengthNumber)) {
    return '??:??'
  }
  const minutes = Math.floor(artifactAudioLengthNumber / 60)
  const seconds = String(Math.ceil(artifactAudioLengthNumber - minutes * 60)).padStart(2, 0)
  return `${minutes}:${seconds}`
}

/**
 * Get a URL to the specified artifact in the Memories app
 * Handles getting the correct base URL based on window location or session type
 * @export
 * @param {number} artifactId
 * @returns {string} URL to artifact in Memories
 */
export function getMemoriesUrl(artifactId) {
  return `${getFrontendBaseUrl(true)}/photos/artifacts/${artifactId}`
}

/**
 * Get a URL to the specified artifact info page in dmc
 * @export
 * @param {number} artifactId
 * @returns {string} URL to artifact in Memories
 */
export function getArtifactInfoPageUrl(artifactId) {
  return `${getFrontendBaseUrl()}/artifacts/artifact/${artifactId}`
}

/**
 * Get a URL to the specified tombstoned artifact info page in dmc
 * @export
 * @param {number} artifactId
 * @param {number} patronId
 * @returns {string} URL to artifact in Memories
 */
export function getTombstonedArtifactInfoPageUrl(artifactId, patronId) {
  return `${getFrontendBaseUrl()}/artifacts/tombstoned/${patronId}/${artifactId}`
}

/**
 *
 * @param {string} input in question
 * @return {boolean} true if valid prefix, false if invalid prefix
 */
export function isValidPrefix(input) {
  switch (input) {
    case 'kw.':
    case 'a.': // Artifact ID: go to artifact info page for “a.123456”
    case 'cis.user.': // CIS User ID: “cis.user.MMMM-MMMM”
    case 'cis.wkca.': // CIS WKCA ID: “cis.wkca.MMMM-MMMM”
    case 'un.': // User Name: “un.someusername”
    case 'mp.': // Memories Patron ID: “mp.123456”
    case 'u.': // Tree Contributor ID: “u.MMDZ-11L” or “u.10000123”
    case 'cn.': // Contact Name: “cn.mycontactname
      return true
    default:
      // Unsupported search prefix
      return false
  }
}

/**
 * Get an internal route for a search query (Artifact, User, or Artifact Search)
 *
 * @export
 * @param {string} prefix gives context to the type of search
 * @param {string} query search, patron, or artifact query, e.g. “birthday party”, “mp.123”, or “a.12345”
 * @returns {?string} route for query, or null if query had invalid search prefix
 */
export function getSearchRoute(prefix, query) {
  switch (prefix) {
    case 'kw.': // Keyword: retrieve artifacts with keyword kw.penguins
      return `/artifacts/search/${encodeURIComponent(query)}`
    case 'a.': // Artifact ID: go to artifact info page for “a.123456”
      return `/artifacts/artifact/${query}`
    case 'cis.user.': // CIS User ID: “cis.user.MMMM-MMMM”
    case 'cis.wkca.': // CIS WKCA ID: “cis.wkca.MMMM-MMMM”
    case 'un.': // User Name: “un.someusername”
    case 'mp.': // Memories Patron ID: “mp.123456”
    case 'u.': // Tree Contributor ID: “u.MMDZ-11L” or “u.10000123”
    case 'cn.': // Contact Name: “cn.mycontactname
      return `/artifacts/patron/${prefix}${query}`
    default:
      // Unsupported search prefix
      return null
  }
}

/**
 *
 * @returns yesterday's date to be used in date picker's default date
 */
export function getYesterdaysDate() {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday
}
